// src/pages/AIAnalysisPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import AnalysisHistory from '../components/AIAnalysis/AnalysisHistory';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import MobileAtmosphericLighting from '../components/ui/MobileAtmosphericLighting';
import { Sparkles, Calendar, Clock, RefreshCw, AlertCircle } from 'lucide-react';

interface AnalysisStats {
  tradeCount: number;
  symbols: string[];
  dates?: {
    first: string;
    last: string;
  };
  winCount: number;
  lossCount: number;
}

interface QuotaRemaining {
  questionsRemaining: number;
  analysesRemaining: number;
  nextAvailable: string | null;
  subscriptionTier: string;
}

interface AnalysisData {
  id?: string;
  analysis: string;
  stats: AnalysisStats;
  created_at?: string;
  timeframe?: string;
  cached?: boolean;
  question?: string;
  quotaRemaining?: QuotaRemaining;
}

const AIAnalysisPage: React.FC = () => {
  const { user } = useAuth();
  const [timeframe, setTimeframe] = useState<string>('last30days');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaMessage, setQuotaMessage] = useState<string | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<QuotaRemaining | null>(null);
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [cachedResult, setCachedResult] = useState<boolean>(false);
  const [question, setQuestion] = useState<string>('');
  const [isQuestionMode, setIsQuestionMode] = useState<boolean>(false);

  // AbortController refs to cancel stale requests
  const analysisControllerRef = useRef<AbortController | null>(null);
  const loadAnalysisControllerRef = useRef<AbortController | null>(null);
  const subscriptionControllerRef = useRef<AbortController | null>(null);

  // Cleanup function to abort all ongoing requests
  const abortAllRequests = () => {
    if (analysisControllerRef.current) {
      analysisControllerRef.current.abort();
      analysisControllerRef.current = null;
    }
    if (loadAnalysisControllerRef.current) {
      loadAnalysisControllerRef.current.abort();
      loadAnalysisControllerRef.current = null;
    }
    if (subscriptionControllerRef.current) {
      subscriptionControllerRef.current.abort();
      subscriptionControllerRef.current = null;
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      abortAllRequests();
    };
  }, []);

  // Check subscription tier with abort support
  useEffect(() => {
    const checkSubscriptionTier = async () => {
      if (!user) return;
      
      // Cancel previous subscription check
      if (subscriptionControllerRef.current) {
        subscriptionControllerRef.current.abort();
      }
      
      // Create new AbortController for this request
      const controller = new AbortController();
      subscriptionControllerRef.current = controller;
      
      try {
        const { data, error } = await supabase
          .from('analysis_usage')
          .select('subscription_tier')
          .eq('user_id', user.id)
          .abortSignal(controller.signal)
          .single();
          
        // Check if request was aborted
        if (controller.signal.aborted) return;
          
        if (error) throw error;
        
        if (data?.subscription_tier === 'yearly') {
          setQuotaMessage(isQuestionMode 
            ? 'As a yearly subscriber, you can ask 5 questions per day.'
            : 'As a yearly subscriber, you can generate 1 analysis per day.');
        } else {
          setQuotaMessage(isQuestionMode
            ? 'As a monthly subscriber, you can ask 2 questions every 48 hours.'
            : 'As a monthly subscriber, you can generate 1 analysis every 48 hours.');
        }
      } catch (err: any) {
        // Don't log errors for aborted requests
        if (err.name === 'AbortError') return;
        console.error('Error checking subscription tier:', err);
      } finally {
        // Clear the controller ref if this was the active request
        if (subscriptionControllerRef.current === controller) {
          subscriptionControllerRef.current = null;
        }
      }
    };
    
    checkSubscriptionTier();
  }, [user, isQuestionMode]);

  const generateAnalysis = async () => {
    if (!user) return;
    
    // Cancel previous analysis request
    if (analysisControllerRef.current) {
      analysisControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const controller = new AbortController();
    analysisControllerRef.current = controller;
    
    setLoading(true);
    setError(null);
    setCachedResult(false);
    
    try {
      // FIXED: Get user's session token for authentication
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.access_token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      const response = await fetch('/.netlify/functions/analyze-trades', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          // FIXED: Add Authorization header with JWT token
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
          // REMOVED: userId no longer needed - backend gets it from JWT
          timeframe,
          question: isQuestionMode ? question : undefined
        }),
        signal: controller.signal
      });
      
      // Check if request was aborted
      if (controller.signal.aborted) return;
      
      const data = await response.json();
      
      // Check again after async operation
      if (controller.signal.aborted) return;
      
      if (response.status === 429) {
        // Quota exceeded
        setError(data.message || 'Analysis quota exceeded');
        if (data.quotaRemaining) {
          setQuotaInfo(data.quotaRemaining);
        }
        return;
      }
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to analyze trades');
      }
      
      // Check if result was cached
      if (data.cached) {
        setCachedResult(true);
      }
      
      // Update quota info if provided
      if (data.quotaRemaining) {
        setQuotaInfo(data.quotaRemaining);
      }
      
      setAnalysisData({
        id: data.id,
        analysis: data.analysis,
        stats: data.stats,
        created_at: new Date().toISOString(),
        timeframe,
        cached: data.cached,
        question: isQuestionMode ? question : undefined,
        quotaRemaining: data.quotaRemaining
      });
      
      // Clear question if successful
      if (isQuestionMode) {
        setQuestion('');
      }
    } catch (err: any) {
      // Don't show errors for aborted requests
      if (err.name === 'AbortError') return;
      
      setError(err.message || 'An error occurred while analyzing your trades');
      console.error('Analysis error:', err);
    } finally {
      // Only update loading state if this was the active request
      if (analysisControllerRef.current === controller) {
        setLoading(false);
        analysisControllerRef.current = null;
      }
    }
  };

  const loadSavedAnalysis = async (id: string) => {
    if (!user) return;
    
    // Cancel previous load request
    if (loadAnalysisControllerRef.current) {
      loadAnalysisControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const controller = new AbortController();
    loadAnalysisControllerRef.current = controller;
    
    setLoading(true);
    setError(null);
    setCachedResult(false);
    
    try {
      const { data, error } = await supabase
        .from('analysis_history')
        .select('*')
        .eq('id', id)
        .abortSignal(controller.signal)
        .single();
      
      // Check if request was aborted
      if (controller.signal.aborted) return;
      
      if (error) throw error;
      
      if (data) {
        setAnalysisData({
          id: data.id,
          analysis: data.analysis,
          stats: data.stats,
          created_at: data.created_at,
          timeframe: data.timeframe,
          question: data.question
        });
        // Set mode based on whether this was a question or analysis
        setIsQuestionMode(!!data.question);
      }
    } catch (err: any) {
      // Don't show errors for aborted requests
      if (err.name === 'AbortError') return;
      
      setError(err.message || 'Failed to load saved analysis');
      console.error('Error loading saved analysis:', err);
    } finally {
      // Only update loading state if this was the active request
      if (loadAnalysisControllerRef.current === controller) {
        setLoading(false);
        loadAnalysisControllerRef.current = null;
      }
    }
  };

  const getWinRate = (stats: AnalysisStats) => {
    if (stats.tradeCount === 0) return 0;
    return Math.round((stats.winCount / stats.tradeCount) * 100);
  };

  return (
    <div className="min-h-screen bg-transparent p-4">
      <div className="relative">
        <MobileAtmosphericLighting />
        
        <Card className="glass-card shadow-lg border border-gray-300/50 dark:border-gray-600/50 backdrop-blur-[100px] bg-white/30 dark:bg-gray-800/30 w-[98%] md:w-[99%] max-w-[1800px] md:max-w-[2000px] mx-auto">
            <div className="px-4 sm:px-6 md:px-8 py-6 md:py-8 w-full max-w-full">
              <div className="flex flex-col md:flex-row items-start justify-between mb-6 w-full">
                <div className="w-full md:w-auto">
                  <h1 className="text-2xl md:text-3xl font-bold flex items-center mb-2 text-gray-900 dark:text-gray-100">
                    <Sparkles className="h-5 w-5 md:h-6 md:w-6 mr-2 text-primary-500 flex-shrink-0" />
                    <span className="break-words">AI Trading Coach</span>
                  </h1>
                  <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">
                    Get personalized insights and advice to improve your trading based on your journal data.
                  </p>
                </div>
                
                <div className="mt-4 md:mt-0 w-full md:w-auto">
                  <Button 
                    variant="outline"
                    onClick={() => setShowHistory(!showHistory)}
                    className="w-full md:w-auto bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                  >
                    {showHistory ? 'Hide History' : 'Show History'}
                  </Button>
                </div>
              </div>

              {/* Instructions block - FIXED */}
              <div className="mb-6 p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg w-full max-w-full overflow-hidden">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">For the best experience:</h3>
                <ul className="space-y-2 text-sm md:text-base text-gray-700 dark:text-gray-300">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 flex-shrink-0">•</span>
                    <span>Use the <strong>Generate Analysis</strong> mode for comprehensive reviews of your trading</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 flex-shrink-0">•</span>
                    <span>Use the <strong>Ask a Question</strong> mode for specific insights about patterns you've noticed</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 flex-shrink-0">•</span>
                    <span>Review your analysis history to track how your trading evolves over time</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 flex-shrink-0">•</span>
                    <span>Generate a new analysis after completing several new trades to get updated insights</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1 flex-shrink-0">•</span>
                    <span><strong>The larger the sample of trades and details, the better the AI analysis!</strong></span>
                  </li>
                </ul>
              </div>

              <div className={`grid gap-6 w-full max-w-full ${showHistory ? 'lg:grid-cols-5' : 'grid-cols-1'}`}>
                <div className={`w-full max-w-full ${showHistory ? 'lg:col-span-3' : 'col-span-1'}`}>
                  <div className="p-4 md:p-6 bg-gray-100/30 dark:bg-gray-700/30 border border-gray-200/50 dark:border-gray-600/50 rounded-xl w-full max-w-full overflow-hidden">
                    {/* Mode toggle - FIXED */}
                    <div className="mb-4 w-full">
                      <div className="bg-gray-200/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-1 shadow-md inline-flex w-full sm:w-auto">
                        <button 
                          className={`px-3 md:px-4 py-2 rounded-lg transition-all font-medium text-sm md:text-base flex-1 sm:flex-none ${
                            !isQuestionMode 
                              ? 'bg-white dark:bg-gray-600 border border-primary-200 dark:border-primary-500 shadow-md text-primary-700 dark:text-primary-300 font-semibold' 
                              : 'text-gray-600 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-gray-600/50 hover:text-gray-700 dark:hover:text-gray-200'
                          }`}
                          onClick={() => setIsQuestionMode(false)}
                        >
                          Generate Analysis
                        </button>
                        <button 
                          className={`px-3 md:px-4 py-2 rounded-lg transition-all font-medium text-sm md:text-base flex-1 sm:flex-none ${
                            isQuestionMode 
                              ? 'bg-white dark:bg-gray-600 border border-primary-200 dark:border-primary-500 shadow-md text-primary-700 dark:text-primary-300 font-semibold' 
                              : 'text-gray-600 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-gray-600/50 hover:text-gray-700 dark:hover:text-gray-200'
                          }`}
                          onClick={() => setIsQuestionMode(true)}
                        >
                          Ask a Question
                        </button>
                      </div>
                    </div>

                    {/* Question input - FIXED */}
                    {isQuestionMode && (
                      <div className="mb-6 w-full max-w-full">
                        <label htmlFor="question" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Your Question
                        </label>
                        <div className="flex items-start gap-2 w-full max-w-full">
                          <input
                            type="text"
                            id="question"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="e.g., Why am I losing on Thursdays? What's my most profitable symbol?"
                            className="flex-1 min-w-0 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Ask a specific question about your trading performance
                        </p>
                      </div>
                    )}
                    
                    <h2 className="text-lg md:text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Select Timeframe</h2>
                    
                    {quotaMessage && (
                      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30 rounded-lg flex items-start w-full max-w-full overflow-hidden">
                        <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="text-sm md:text-base break-words">{quotaMessage}</p>
                      </div>
                    )}
                    
                    {/* Timeframe buttons - FIXED */}
                    <div className="grid grid-cols-2 lg:flex lg:flex-wrap gap-2 mb-6 w-full max-w-full">
                      <button
                        onClick={() => setTimeframe('today')}
                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-all border text-sm md:text-base ${
                          timeframe === 'today'
                            ? 'bg-primary-600 hover:bg-primary-700 text-white border-primary-600 shadow-md'
                            : 'bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        Today
                      </button>
                      <button
                        onClick={() => setTimeframe('last7days')}
                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-all border text-sm md:text-base ${
                          timeframe === 'last7days'
                            ? 'bg-primary-600 hover:bg-primary-700 text-white border-primary-600 shadow-md'
                            : 'bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        Last 7 Days
                      </button>
                      <button
                        onClick={() => setTimeframe('last30days')}
                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-all border text-sm md:text-base ${
                          timeframe === 'last30days'
                            ? 'bg-primary-600 hover:bg-primary-700 text-white border-primary-600 shadow-md'
                            : 'bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        Last 30 Days
                      </button>
                      <button
                        onClick={() => setTimeframe('lastQuarter')}
                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-all border text-sm md:text-base ${
                          timeframe === 'lastQuarter'
                            ? 'bg-primary-600 hover:bg-primary-700 text-white border-primary-600 shadow-md'
                            : 'bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        Last Quarter
                      </button>
                      <button
                        onClick={() => setTimeframe('allTime')}
                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-all border text-sm md:text-base ${
                          timeframe === 'allTime'
                            ? 'bg-primary-600 hover:bg-primary-700 text-white border-primary-600 shadow-md'
                            : 'bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        All Time
                      </button>
                    </div>
                    
                    {/* Generate Analysis button - FIXED */}
                    <button
                      onClick={generateAnalysis}
                      disabled={loading || (isQuestionMode && !question.trim())}
                      className={`w-full sm:w-auto flex items-center justify-center px-6 py-2 !rounded-full shadow-md hover:shadow-lg transition-all duration-300 font-medium text-sm md:text-base ${
                        loading || (isQuestionMode && !question.trim())
                          ? 'bg-gray-400 dark:bg-gray-600 text-white cursor-not-allowed border border-gray-400 dark:border-gray-600' 
                          : 'bg-primary-600 hover:bg-primary-700 text-white border border-primary-600'
                      }`}
                    >
                      {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin flex-shrink-0" /> : <Sparkles className="h-4 w-4 mr-2 flex-shrink-0" />}
                      {isQuestionMode ? 'Ask Question' : 'Generate Analysis'}
                    </button>
                    
                    {error && (
                      <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/30 rounded-md flex items-start w-full max-w-full overflow-hidden">
                        <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium break-words">{error}</p>
                          {quotaInfo?.nextAvailable && (
                            <p className="mt-1 text-sm break-words">Next available: {quotaInfo.nextAvailable}</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {cachedResult && !error && (
                      <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800/30 rounded-md flex items-start w-full max-w-full overflow-hidden">
                        <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="text-sm md:text-base break-words">Using cached analysis - no new trades found since your last analysis of this timeframe.</p>
                      </div>
                    )}
                    
                    {analysisData && (
                      <div className="mt-6 w-full max-w-full overflow-hidden">
                        <div className="flex flex-col sm:flex-row items-start justify-between mb-4 gap-2">
                          <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100">Analysis Results</h3>
                          {analysisData.created_at && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center flex-shrink-0">
                              <Clock className="h-4 w-4 mr-1 flex-shrink-0" />
                              <span className="whitespace-nowrap">{new Date(analysisData.created_at).toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                        
                        {analysisData.stats && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-6 w-full max-w-full">
                            <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600/50">
                              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Trades</div>
                              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{analysisData.stats.tradeCount}</div>
                            </div>
                            
                            <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600/50">
                              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Win Rate</div>
                              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{getWinRate(analysisData.stats)}%</div>
                            </div>
                            
                            <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600/50">
                              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Wins</div>
                              <div className="text-lg sm:text-2xl font-bold text-green-600 dark:text-green-400">{analysisData.stats.winCount}</div>
                            </div>
                            
                            <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600/50">
                              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Losses</div>
                              <div className="text-lg sm:text-2xl font-bold text-red-600 dark:text-red-400">{analysisData.stats.lossCount}</div>
                            </div>
                          </div>
                        )}
                        
                        <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600/50 rounded-lg p-4 sm:p-6 w-full max-w-full overflow-hidden">
                          <h4 className="font-semibold mb-4 text-gray-900 dark:text-gray-100">Coach's Analysis</h4>
                          
                          {analysisData.question && (
                            <div className="mb-4 bg-gray-100 dark:bg-gray-600/30 border border-gray-200 dark:border-gray-600/50 p-4 rounded-lg w-full max-w-full overflow-hidden">
                              <p className="font-medium text-gray-700 dark:text-gray-300">Your question:</p>
                              <p className="text-gray-800 dark:text-gray-200 break-words">{analysisData.question}</p>
                            </div>
                          )}
                          
                          <div className="whitespace-pre-line text-gray-900 dark:text-gray-100 text-sm md:text-base break-words">
                            {analysisData.analysis}
                          </div>
                        </div>
                        
                        {analysisData.stats?.symbols && analysisData.stats.symbols.length > 0 && (
                          <div className="mt-4 w-full max-w-full overflow-hidden">
                            <h4 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">Symbols Traded</h4>
                            <div className="flex flex-wrap gap-2 w-full max-w-full">
                              {analysisData.stats.symbols.map(symbol => (
                                <span key={symbol} className="bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 border border-primary-200 dark:border-primary-700/50 px-2 py-1 rounded text-xs sm:text-sm whitespace-nowrap">
                                  {symbol}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {showHistory && (
                  <div className="lg:col-span-2 w-full max-w-full">
                    <div className="p-4 md:p-6 bg-gray-100/30 dark:bg-gray-700/30 border border-gray-200/50 dark:border-gray-600/50 rounded-xl w-full max-w-full overflow-hidden">
                      <AnalysisHistory onSelectAnalysis={loadSavedAnalysis} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
      </div>
    </div>
  );
};

export default AIAnalysisPage;